import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, ExcelIcon, exportToExcel } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './securityScreens.css';

// G-L-15 — Cihaz Envanteri. İki sekme (§9):
//   Cihazlar    → cihaz başına satır
//   Temsilciler → temsilci başına satır (vw_RepresentativeDevice); CİHAZSIZ temsilciler DE görünür,
//                 eksik cihaz tespiti bunu gerektirir.
//
// Kayıtlar mobil girişte otomatik oluşur; bu ekranda cihaz EKLENMEZ. Yalnız destek notu,
// "sahadan çekildi" işareti, zimmet ve mükerrer birleştirme yönetilir.

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('tr-TR') : '-');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');
const mb = (v) => (v == null ? null : v >= 1024 ? `${(v / 1024).toFixed(1)} GB` : `${v} MB`);

const STATUS_TONE = {
  'EŞLEŞİYOR': 'ok',
  'ZİMMET FARKLI': 'danger',
  'ZİMMET YOK': 'warn',
  'CİHAZ YOK': 'muted',
};

/** Okunamayan alan (iOS'ta batarya/ABI) ile gerçekten 0 olan değer karışmamalı. */
function Spec({ label, value, unit }) {
  const empty = value == null || value === '' || value === '?';
  return (
    <>
      <dt>{label}</dt>
      <dd className={empty ? 'na' : undefined}>{empty ? 'okunamıyor' : `${value}${unit || ''}`}</dd>
    </>
  );
}

function DistList({ items, nameOf, max }) {
  return (
    <div className="sec-dist">
      {items.slice(0, 5).map((it, i) => (
        <div className="sec-dist-row" key={i}>
          <span className="sec-dist-name" title={nameOf(it)}>{nameOf(it)}</span>
          <span className="sec-dist-bar">
            <span className="sec-dist-fill" style={{ width: `${max ? (it.count / max) * 100 : 0}%` }} />
          </span>
          <span className="sec-dist-count">{it.count}</span>
        </div>
      ))}
      {items.length === 0 && <div className="sec-card-hint">Veri yok</div>}
    </div>
  );
}

export function DevicesPage() {
  usePageMeta('Cihaz Envanteri', 'Güvenlik & İzleme');
  const { canEdit } = usePageActions();

  const [tab, setTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [reps, setReps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [drawer, setDrawer] = useState(null);       // açık cihaz künyesi
  const [drawerUsers, setDrawerUsers] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [retiredDraft, setRetiredDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const pull = async (url) => {
    const res = await fetch(url, { headers: authHeaders() });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.message || body?.Message || 'İstek başarısız');
    return body;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devBody, sumBody, repBody] = await Promise.all([
        pull(`/api/devices?$top=500&$orderby=LastSeenAt desc&includeRetired=${includeRetired}`),
        pull('/api/devices/summary'),
        pull('/api/devices/by-representative'),
      ]);
      setDevices(devBody?.value || devBody?.data || []);
      setSummary(sumBody?.data || sumBody?.Data || null);
      setReps(repBody?.data || repBody?.Data || []);
    } catch (e) {
      showToast('error', e.message || 'Envanter okunamadı');
    } finally {
      setLoading(false);
    }
  }, [includeRetired]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (d) => {
    setDrawer(d);
    setNoteDraft(d.notes || '');
    setRetiredDraft(!!d.isRetired);
    setDrawerUsers([]);
    try {
      const body = await pull(`/api/devices/${d.id}/users`);
      setDrawerUsers(body?.data || body?.Data || []);
    } catch { /* kullanıcı listesi opsiyonel — künye yine gösterilir */ }
  };

  const saveDrawer = async () => {
    if (!drawer) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/devices/${drawer.id}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ notes: noteDraft, isRetired: retiredDraft }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      showToast('success', 'Cihaz güncellendi');
      setDrawer(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const q = search.trim().toLocaleLowerCase('tr');
  const visibleDevices = useMemo(() => (!q ? devices : devices.filter((d) =>
    [d.model, d.manufacturer, d.shortCode, d.osVersion, d.appVersion]
      .some((v) => String(v || '').toLocaleLowerCase('tr').includes(q)))), [devices, q]);

  const visibleReps = useMemo(() => {
    let r = reps;
    if (statusFilter) r = r.filter((x) => x.assignmentStatus === statusFilter);
    if (q) {
      r = r.filter((x) => [x.representativeCode, x.representativeName, x.model, x.distributorName]
        .some((v) => String(v || '').toLocaleLowerCase('tr').includes(q)));
    }
    return r;
  }, [reps, statusFilter, q]);

  const maxOs = Math.max(1, ...(summary?.byOs || []).map((x) => x.count));
  const maxModel = Math.max(1, ...(summary?.byModel || []).map((x) => x.count));
  const maxApp = Math.max(1, ...(summary?.byAppVersion || []).map((x) => x.count));

  const handleExport = () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    if (tab === 'devices') {
      exportToExcel(visibleDevices.map((d) => ({
        'Kısa kod': d.shortCode, 'Üretici': d.manufacturer, 'Model': d.modelDisplayName || d.model,
        'OS': `${d.osName || ''} ${d.osVersion || ''}`.trim(), 'RAM': mb(d.totalRamMb) || '',
        'Boş depolama': mb(d.availableStorageMb) || '', 'Uygulama': d.appVersion,
        'Giriş sayısı': d.loginCount, 'Son görülme': fmtDateTime(d.lastSeenAt),
        'Sahadan çekildi': d.isRetired ? 'Evet' : 'Hayır', 'Not': d.notes || '',
      })), null, `cihaz-envanteri_${dateStr}`);
    } else {
      exportToExcel(visibleReps.map((r) => ({
        'Temsilci kodu': r.representativeCode, 'Temsilci': r.representativeName,
        'Distribütör': r.distributorName, 'Kullanıcı': r.userFullName,
        'Cihaz': r.model || '', 'Kısa kod': r.shortCode || '',
        'OS': `${r.osName || ''} ${r.osVersion || ''}`.trim(), 'RAM': mb(r.totalRamMb) || '',
        'Uygulama': r.appVersion || '', 'Farklı cihaz sayısı': r.deviceCount,
        'Son görülme': fmtDateTime(r.lastSeenAt), 'Zimmet durumu': r.assignmentStatus,
      })), null, `temsilci-cihaz_${dateStr}`);
    }
    showToast('success', 'Dışa aktarıldı');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder={tab === 'devices' ? 'Model, üretici, kısa kod veya sürüm…' : 'Temsilci, kod, distribütör veya model…'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {search && <button style={s.btnSecondary} onClick={() => setSearch('')}>Temizle</button>}
      {tab === 'devices' && (
        <label className="sec-inline-check" style={{ marginTop: 0 }}>
          <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} />
          Sahadan çekilenleri de göster
        </label>
      )}
      {tab === 'reps' && (
        <select className="gp-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tüm zimmet durumları</option>
          {Object.keys(STATUS_TONE).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      )}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
        ><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Cihaz Envanteri"
        subtitle="Saha cihazlarının künyesi her mobil girişte otomatik güncellenir — kullanıcıdan hiçbir bilgi istenmez."
        toolbar={toolbarNode}
      >
        <div className="sec-cards">
          <div className="sec-card">
            <div className="sec-card-label">Aktif cihaz</div>
            <div className="sec-card-value">{loading ? '…' : (summary?.total ?? 0)}</div>
            <div className="sec-card-hint">{summary?.retired ?? 0} cihaz sahadan çekilmiş</div>
          </div>
          <div className={`sec-card${(summary?.staleOver30Days ?? 0) > 0 ? ' sec-card--alert' : ''}`}>
            <div className="sec-card-label">30 gündür görülmeyen</div>
            <div className="sec-card-value">{loading ? '…' : (summary?.staleOver30Days ?? 0)}</div>
            <div className="sec-card-hint">Kayıp veya arızalı olabilir</div>
          </div>
          <div className="sec-card">
            <div className="sec-card-label">OS sürümü</div>
            <DistList items={summary?.byOs || []} max={maxOs} nameOf={(x) => `${x.osName} ${x.osVersion}`} />
          </div>
          <div className="sec-card">
            <div className="sec-card-label">Model</div>
            <DistList items={summary?.byModel || []} max={maxModel} nameOf={(x) => `${x.manufacturer || ''} ${x.model || ''}`.trim() || '?'} />
          </div>
          <div className="sec-card">
            <div className="sec-card-label">Uygulama sürümü</div>
            <DistList items={summary?.byAppVersion || []} max={maxApp} nameOf={(x) => x.appVersion || '?'} />
          </div>
        </div>

        <div className="sec-tabs">
          <button type="button" className={`sec-tab${tab === 'devices' ? ' on' : ''}`} onClick={() => setTab('devices')}>
            Cihazlar
          </button>
          <button type="button" className={`sec-tab${tab === 'reps' ? ' on' : ''}`} onClick={() => setTab('reps')}>
            Temsilciler
          </button>
        </div>

        {tab === 'devices' ? (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Kısa kod</th><th>Model</th><th>OS</th><th>RAM</th><th>Boş depolama</th>
                <th>Uygulama</th><th>Giriş</th><th>Son görülme</th><th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
              {!loading && visibleDevices.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>Cihaz kaydı bulunamadı</td></tr>
              )}
              {!loading && visibleDevices.map((d) => (
                <tr key={d.id} onClick={() => openDrawer(d)} style={{ cursor: 'pointer' }}>
                  <td><span className="c360-erp-chip">{d.shortCode}</span></td>
                  <td>{d.modelDisplayName || d.model || '-'}<div className="sec-card-hint">{d.manufacturer}</div></td>
                  <td>{`${d.osName || ''} ${d.osVersion || ''}`.trim() || '-'}</td>
                  <td>{mb(d.totalRamMb) || '-'}</td>
                  <td>{mb(d.availableStorageMb) || '-'}</td>
                  <td>{d.appVersion || '-'}</td>
                  <td>{d.loginCount}</td>
                  <td>{fmtDateTime(d.lastSeenAt)}</td>
                  <td>{d.isRetired
                    ? <span className="sec-badge sec-badge--muted">ÇEKİLDİ</span>
                    : <span className="sec-badge sec-badge--ok">SAHADA</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Temsilci</th><th>Distribütör</th><th>Kullanıcı</th><th>Cihaz</th>
                <th>OS</th><th>RAM</th><th>Farklı cihaz</th><th>Son görülme</th><th>Zimmet</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
              {!loading && visibleReps.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>Temsilci bulunamadı</td></tr>
              )}
              {!loading && visibleReps.map((r) => (
                <tr key={r.representativeId}>
                  <td><span className="c360-erp-chip">{r.representativeCode}</span>
                    <div className="sec-card-hint">{r.representativeName}</div></td>
                  <td>{r.distributorName || '-'}</td>
                  <td>{r.userFullName || '-'}</td>
                  <td>{r.model || <span className="sec-card-hint">cihaz yok</span>}</td>
                  <td>{`${r.osName || ''} ${r.osVersion || ''}`.trim() || '-'}</td>
                  <td>{mb(r.totalRamMb) || '-'}</td>
                  {/* 1'den büyükse temsilci birden fazla cihazdan giriyor — incelenmeli. */}
                  <td>{r.deviceCount > 1
                    ? <span className="sec-badge sec-badge--warn">{r.deviceCount}</span>
                    : (r.deviceCount || 0)}</td>
                  <td>{fmtDateTime(r.lastSeenAt)}</td>
                  <td><span className={`sec-badge sec-badge--${STATUS_TONE[r.assignmentStatus] || 'muted'}`}>
                    {r.assignmentStatus}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListPageShell>

      {drawer && (
        <>
          <div className="sec-drawer-backdrop" onClick={() => setDrawer(null)} />
          <aside className="sec-drawer">
            <div className="sec-drawer-head">
              <h3 className="sec-drawer-title">{drawer.modelDisplayName || drawer.model || 'Cihaz'}</h3>
              <span className="c360-erp-chip">{drawer.shortCode}</span>
              <button type="button" style={s.btnSecondary} onClick={() => setDrawer(null)}>Kapat</button>
            </div>
            <div className="sec-drawer-body">
              <div className="sec-spec-group">Donanım</div>
              <dl className="sec-spec">
                <Spec label="Üretici" value={drawer.manufacturer} />
                <Spec label="Marka" value={drawer.brand} />
                <Spec label="Model" value={drawer.model} />
                <Spec label="CPU mimarisi" value={drawer.cpuAbi} />
                <Spec label="Çekirdek" value={drawer.cpuCoreCount} />
                <Spec label="Toplam RAM" value={mb(drawer.totalRamMb)} />
                <Spec label="Toplam depolama" value={mb(drawer.totalStorageMb)} />
                <Spec label="Ekran" value={drawer.screenResolution} />
              </dl>

              <div className="sec-spec-group">Yazılım</div>
              <dl className="sec-spec">
                <Spec label="İşletim sistemi" value={`${drawer.osName || ''} ${drawer.osVersion || ''}`.trim()} />
                <Spec label="API seviyesi" value={drawer.osApiLevel} />
                <Spec label="Uygulama sürümü" value={drawer.appVersion} />
                <Spec label="Build" value={drawer.appBuildNumber} />
              </dl>

              <div className="sec-spec-group">Son girişteki durum</div>
              <dl className="sec-spec">
                <Spec label="Boş RAM" value={mb(drawer.availableRamMb)} />
                <Spec label="Boş depolama" value={mb(drawer.availableStorageMb)} />
                {/* iOS'ta batarya sağlığı uygulamaya kapalıdır — orada daima "okunamıyor" görünür. */}
                <Spec label="Batarya sağlığı" value={drawer.batteryHealthPercent} unit="%" />
                <Spec label="Root/Jailbreak" value={drawer.isRooted == null ? null : (drawer.isRooted ? 'Şüpheli' : 'Hayır')} />
              </dl>

              <div className="sec-spec-group">Takip</div>
              <dl className="sec-spec">
                <Spec label="İlk görülme" value={fmtDate(drawer.firstSeenAt)} />
                <Spec label="Son görülme" value={fmtDateTime(drawer.lastSeenAt)} />
                <Spec label="Toplam giriş" value={drawer.loginCount} />
              </dl>

              <div className="sec-spec-group">Bu cihazı kullananlar</div>
              {drawerUsers.length === 0
                ? <div className="sec-card-hint">Giriş kaydı bulunamadı</div>
                : (
                  <table className="c360-table" style={{ width: '100%' }}>
                    <thead><tr><th>Kullanıcı</th><th>Temsilci</th><th>Giriş</th><th>Son</th></tr></thead>
                    <tbody>
                      {drawerUsers.map((u) => (
                        <tr key={u.userId}>
                          <td>{u.userFullName || '-'}</td>
                          <td>{u.representativeCode || '-'}</td>
                          <td>{u.loginCount}</td>
                          <td>{fmtDate(u.lastSeen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              <div className="sec-spec-group">Destek notu</div>
              <textarea
                className="sec-note-input"
                value={noteDraft}
                disabled={!canEdit}
                placeholder="Örn. değişim planlandı, ekran çatlak…"
                onChange={(e) => setNoteDraft(e.target.value)}
              />
              <label className="sec-inline-check">
                <input
                  type="checkbox"
                  checked={retiredDraft}
                  disabled={!canEdit}
                  onChange={(e) => setRetiredDraft(e.target.checked)}
                />
                Sahadan çekildi (listede varsayılan olarak gizlenir)
              </label>
            </div>
            <div className="sec-drawer-foot">
              {canEdit && (
                <button type="button" className="dps-btn primary" style={s.btnPrimary} disabled={saving} onClick={saveDrawer}>
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDrawer(null)}>Vazgeç</button>
            </div>
          </aside>
        </>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default DevicesPage;
